import { Check, Crosshair, Minimize2, MessageSquare, Pencil, UserCog, X } from "lucide-react"
import * as React from "react"

import { Button } from "@/components/ui/button"
import { FormField } from "@/components/ui/form-field"
import { IconButton } from "@/components/ui/icon-button"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"

import { FindingSpine } from "./finding-spine"
import { diffWords, sideParts, type DiffPart } from "./word-diff"
import {
  ORIGIN_LABEL,
  SEVERITY_LABEL,
  VERDICT_LABEL,
  type Finding,
  type FindingAxes,
  type FindingSeverity,
} from "../data/review-data"

/**
 * A single finding in the rail. Renders in one of three modes — collapsed,
 * expanded, or editing — driven by props. Selection and expansion state
 * are lifted to the workspace so the PDF overlay and the rail agree on a
 * single active finding.
 */
const SEVERITY_CHIP: Record<FindingSeverity, string> = {
  error: "bg-err-tint text-err",
  warning: "bg-print-tint text-print",
  info: "bg-info-tint text-info",
}

export interface FindingRecordProps {
  finding: Finding
  selected: boolean
  expanded: boolean
  editing: boolean
  canDispose: boolean
  onSelect: () => void
  /** Zoom the page to this finding's box. Omitted when it has none to frame. */
  onFrame?: () => void
  /** True while the page is framed on this finding, so the control offers to undo. */
  framed?: boolean
  onToggleExpand: () => void
  onEnterEdit: () => void
  onCancelEdit: () => void
  onSave: (patch: { claim: string }) => void
  onConfirm: () => void
  onDismiss: () => void
  onSendToSme: () => void
  onMarkCorrected: () => void
  onOpenComments: () => void
}

export const FindingRecord = React.forwardRef<HTMLLIElement, FindingRecordProps>(
  function FindingRecord(
    {
      finding,
      selected,
      expanded,
      editing,
      canDispose,
      onSelect,
      onFrame,
      framed = false,
      onToggleExpand,
      onEnterEdit,
      onCancelEdit,
      onSave,
      onConfirm,
      onDismiss,
      onSendToSme,
      onMarkCorrected,
      onOpenComments,
    },
    ref,
  ) {
    return (
      <li
        ref={ref}
        data-finding-id={finding.id}
        data-selected={selected || undefined}
        className={cn(
          "group relative overflow-hidden rounded-[10px] border transition-[border-color,box-shadow,background-color] duration-180 ease-out",
          // Selection has to survive a list this long, but the card is already
          // carrying a severity chip, a gutter stripe and an evidence block —
          // filling it as well leaves nothing for the content to stand against.
          // A ring and a lift read at a glance and let the card stay white.
          selected
            ? "border-brand bg-surface shadow-e2 ring-1 ring-brand"
            : "border-border bg-surface hover:border-border-strong hover:shadow-e1",
        )}
      >
        {/* The gutter is a sibling of the expand control, not a child of it: the
            number doubles as the focus button, and a button cannot nest. */}
        <div className="grid grid-cols-[44px_minmax(0,1fr)_auto]">
          <Gutter
            number={finding.number}
            severity={finding.severity}
            axes={finding.axes}
            verdictSummary={`${VERDICT_LABEL[finding.axes.verdict]}, ${ORIGIN_LABEL[finding.axes.origin]}`}
            {...(onFrame ? { onFrame, framed } : {})}
          />
          <button
            type="button"
            onClick={() => {
              if (!selected) onSelect()
              onToggleExpand()
            }}
            aria-expanded={expanded}
            aria-controls={`finding-body-${finding.id}`}
            className="min-w-0 px-3 py-2.5 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[color:var(--brand)]"
          >
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="font-id text-body-sm font-semibold text-text">{finding.ruleId}</span>
              <SeverityChip severity={finding.severity} />
              <PlacementChip finding={finding} />
              {finding.axes.origin === "carried_over" && (
                <MetaChip>Carried over from {finding.firstSeenVersion}</MetaChip>
              )}
              {finding.axes.origin === "regression" && (
                <span className="rounded-sm bg-err-tint px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em] text-err">
                  Regression
                </span>
              )}
              {finding.authoredBy && (
                <span className="rounded-sm bg-brand-tint px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em] text-brand">
                  Manual
                </span>
              )}
              {/* The claim is a reviewer's wording, not the pipeline's. Worth
                  saying on a record that has to be defensible later. */}
              {finding.claimIsOverridden && <MetaChip>Edited</MetaChip>}

              {/* Right-aligned: what a finding is reads left, what was decided reads right. */}
              <span className="ml-auto flex shrink-0 items-center gap-1.5">
                {finding.occurrences > 1 && <MetaChip>{finding.occurrences}x on label</MetaChip>}
                <VerdictChip verdict={finding.axes.verdict} />
              </span>
            </div>
            <p className="mt-1.5 text-body-sm leading-snug text-text">{finding.claim}</p>
            <FindingMeta finding={finding} />
          </button>
          {/* A sibling of the expand control, not a child: a button cannot nest,
              and this must stay clickable in both states. Held in the same slot
              whether the card is open or closed so it does not move under the
              pointer when the card expands. */}
          <span className="flex items-start py-2 pr-2">
            <IconButton
              size="sm"
              variant="ghost"
              label={
                finding.commentCount > 0
                  ? `Open comments, ${String(finding.commentCount)} total`
                  : "Open comments"
              }
              icon={<MessageSquare />}
              onClick={onOpenComments}
              // Unread when there is unread, otherwise the total: a finding
              // carrying a read conversation is not the same as one nobody has
              // written on, and only the badge says so at a glance.
              {...(finding.unreadCount > 0
                ? { count: finding.unreadCount, countLabel: "unread" }
                : finding.commentCount > 0
                  ? { count: finding.commentCount, countLabel: "comment" }
                  : {})}
            />
          </span>
        </div>

        {expanded && (
          <div
            id={`finding-body-${finding.id}`}
            className="mx-3 mt-1 border-t border-border-soft pb-3 pt-3"
          >
            {editing ? (
              <EditForm finding={finding} onCancel={onCancelEdit} onSave={onSave} />
            ) : (
              <ReadView
                finding={finding}
                canDispose={canDispose}
                // Anyone who can dispose can reword: PATCH .../summary overlays a
                // reviewer's prose on the generated text for any finding, and it
                // is that prose the client report prints.
                canEdit={canDispose}
                onEnterEdit={onEnterEdit}
                onConfirm={onConfirm}
                onDismiss={onDismiss}
                onSendToSme={onSendToSme}
                onMarkCorrected={onMarkCorrected}
              />
            )}
          </div>
        )}
      </li>
    )
  },
)

const SEVERITY_NUMBER: Record<FindingSeverity, string> = {
  error: "text-err ring-err/25",
  warning: "text-print ring-print/30",
  info: "text-info ring-info/25",
}

/**
 * 44px left column: severity-tinted number badge on a subtle top-fade,
 * then the four-axis spine below it. The number chip's ring + text tone
 * carry the severity, so a reviewer picks up position + severity in one
 * eye-fixation without needing the ERROR/WARNING chip in the body.
 * Border on the right side visually separates the identifier column
 * from the body.
 */
/** Undecided is unmarked — on a review where most findings wait, a chip on each says nothing. */
function VerdictChip({ verdict }: { verdict: FindingAxes["verdict"] }): JSX.Element | null {
  if (verdict === "unset") return null
  const tone =
    verdict === "confirmed"
      ? "bg-master-tint text-master"
      : verdict === "rejected"
        ? "bg-surface-sunken text-text-3"
        : "bg-people-tint text-people"
  return (
    <span
      className={cn(
        "rounded-sm px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em]",
        tone,
      )}
    >
      {VERDICT_LABEL[verdict]}
    </span>
  )
}

/** The compact metadata line beneath a finding's claim. */
function FindingMeta({ finding }: { finding: Finding }): JSX.Element | null {
  const pct = finding.confidence == null ? null : Math.round(finding.confidence * 100)
  const parts: string[] = []
  if (finding.kpis.length > 0) parts.push(`KPI ${finding.kpis.join(", ")}`)
  if (pct != null) parts.push(`Conf: ${String(pct)}%`)
  if (finding.occurrences > 1) parts.push(`${String(finding.occurrences)}x on label`)
  if (parts.length === 0) return null

  return <p className="mt-1 font-id text-[10.5px] tabular-nums text-text-3">{parts.join(" · ")}</p>
}

function Gutter({
  number,
  severity,
  axes,
  verdictSummary,
  onFrame,
  framed = false,
}: {
  number: number
  severity: FindingSeverity
  axes: Finding["axes"]
  verdictSummary: string
  onFrame?: () => void
  framed?: boolean
}): JSX.Element {
  return (
    <span
      // Not aria-hidden when it carries the focus control: the badge is then a
      // real button and has to reach the accessibility tree.
      {...(onFrame ? {} : { "aria-hidden": true })}
      className="group/gutter flex flex-col items-center gap-2.5 border-r border-border-soft bg-gradient-to-b from-surface-sunken/70 to-transparent to-60% py-2.5"
    >
      {onFrame ? (
        /* The number doubles as the focus control: it already marks this finding
           on the page, so it is where a reviewer looks to go there. The glyph
           swaps on hover rather than a second control appearing, which would
           cost the gutter its width. */
        <button
          type="button"
          onClick={onFrame}
          aria-pressed={framed}
          title={framed ? "Reset zoom" : "Zoom the page to this finding"}
          aria-label={framed ? "Reset zoom" : "Zoom the page to this finding"}
          className={cn(
            "group/badge grid size-6 shrink-0 place-items-center rounded-full font-id text-[12.5px] font-semibold leading-none tabular-nums ring-1 ring-inset transition-colors duration-120 ease-out",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-[color:var(--brand)]",
            // Framed is the only state that inverts — it is a mode the reviewer
            // is in and has to be able to leave. Hover stays in the severity
            // palette so the badge keeps reading as this finding's marker.
            framed
              ? "bg-brand text-white ring-brand"
              : cn(SEVERITY_NUMBER[severity], "bg-surface hover:ring-2"),
          )}
        >
          {framed ? (
            <Minimize2 className="size-3" aria-hidden="true" />
          ) : (
            <>
              <span className="group-hover/badge:hidden">{number}</span>
              <Crosshair className="hidden size-3 group-hover/badge:block" aria-hidden="true" />
            </>
          )}
        </button>
      ) : (
        <span
          aria-hidden="true"
          className={cn(
            "grid size-6 place-items-center rounded-full bg-surface font-id text-[12.5px] font-semibold leading-none tabular-nums ring-1 ring-inset",
            SEVERITY_NUMBER[severity],
          )}
        >
          {number}
        </span>
      )}
      <FindingSpine axes={axes} summary={verdictSummary} />
    </span>
  )
}

function SeverityChip({ severity }: { severity: FindingSeverity }): JSX.Element {
  return (
    <span
      className={cn(
        "rounded-sm px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em]",
        SEVERITY_CHIP[severity],
      )}
    >
      {SEVERITY_LABEL[severity]}
    </span>
  )
}

function PlacementChip({ finding }: { finding: Finding }): JSX.Element {
  const label =
    finding.placement === "box"
      ? `Boxed · p.${finding.bbox?.page ?? 1}`
      : finding.placement === "page"
        ? `Page ${finding.pageHint ?? 1}`
        : "Whole label"
  return <MetaChip>{label}</MetaChip>
}

function MetaChip({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="rounded-sm bg-surface-sunken px-1.5 py-0.5 font-id text-[10.5px] font-medium uppercase tracking-[0.06em] text-text-3">
      {children}
    </span>
  )
}

function ReadView({
  finding,
  canDispose,
  canEdit,
  onEnterEdit,
  onConfirm,
  onDismiss,
  onSendToSme,
  onMarkCorrected,
}: {
  finding: Finding
  canDispose: boolean
  canEdit: boolean
  onEnterEdit: () => void
  onConfirm: () => void
  onDismiss: () => void
  onSendToSme: () => void
  onMarkCorrected: () => void
}): JSX.Element {
  const verdict = finding.axes.verdict
  return (
    <div className="space-y-3">
      {finding.divergence && (
        <Section label="Divergence">
          {/* The pipeline's sentence naming the difference, above the two texts
              it is drawn from — a reviewer reads what differs before comparing
              the quotes themselves. */}
          {finding.divergence.statement && (
            <p className="mb-2 text-body-sm leading-[1.55] text-text-2">
              {finding.divergence.statement}
            </p>
          )}
          {(finding.divergence.master != null || finding.divergence.printed != null) && (
            <EvidencePair evidence={finding.divergence} />
          )}
        </Section>
      )}
      {finding.reasoning && (
        <Section label="Why it is a violation">
          <p className="font-doc text-[13px] leading-[1.55] text-text-2">{finding.reasoning}</p>
        </Section>
      )}
      {finding.authorities.length > 0 && (
        <Section label="Authority">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-body-sm text-text-2">
            {finding.authorities.map((a, i) => (
              <React.Fragment key={`${a.kind}-${a.label}-${i}`}>
                {i > 0 && (
                  <span aria-hidden="true" className="text-text-disabled">
                    ·
                  </span>
                )}
                <span className="font-id text-[12px] text-text-2 underline decoration-border-strong decoration-1 underline-offset-2 hover:text-brand hover:decoration-brand">
                  {a.label}
                </span>
              </React.Fragment>
            ))}
          </div>
        </Section>
      )}
      {finding.remediation && (
        <Section label="Remediation">
          <p className="text-body-sm leading-[1.55] text-text-2">{finding.remediation}</p>
        </Section>
      )}
      {/* A specialist's written answer. Tinted because it is a person's words on
          a machine's finding, and it is what the escalation was for. */}
      {finding.smeNote && (
        <Section label="Specialist note">
          <p className="rounded-md border border-people/25 bg-people-tint/50 px-2.5 py-2 text-body-sm leading-[1.55] text-text-2">
            {finding.smeNote.note}
          </p>
        </Section>
      )}
      {finding.dismissalNote && (
        <Section label="Why this was dismissed">
          <p className="rounded-md border border-border bg-surface-sunken px-2.5 py-2 text-body-sm leading-[1.55] text-text-2">
            {finding.dismissalNote.note ?? "No reason recorded."}
          </p>
        </Section>
      )}
      {canDispose ? (
        <div className="flex flex-wrap items-center gap-1.5 border-t border-border-soft pt-3">
          <VerdictButton
            active={verdict === "confirmed"}
            tone="master"
            icon={<Check />}
            label="Confirm"
            onClick={onConfirm}
          />
          <VerdictButton
            active={verdict === "rejected"}
            tone="neutral"
            icon={<X />}
            label="Dismiss"
            onClick={onDismiss}
          />
          <VerdictButton
            active={verdict === "needs_sme"}
            tone="info"
            icon={<UserCog />}
            label="Send to specialist"
            onClick={onSendToSme}
          />
          {finding.axes.state === "candidate_corrected" && (
            <Button size="sm" variant="primary" onClick={onMarkCorrected}>
              Mark corrected
            </Button>
          )}
          {canEdit && (
            <IconButton
              size="sm"
              variant="ghost"
              label="Edit finding"
              icon={<Pencil />}
              onClick={onEnterEdit}
            />
          )}
        </div>
      ) : (
        <p className="rounded-md border border-dashed border-border bg-surface-sunken/60 px-3 py-2 text-caption leading-normal text-text-3">
          Read-only — dispositions are made by reviewers and specialists.
        </p>
      )}
    </div>
  )
}

const VERDICT_TONE: Record<"master" | "neutral" | "info", { active: string; inactive: string }> = {
  master: {
    active: "border-master bg-master-tint text-master",
    inactive:
      "border-border text-text-2 hover:border-master/40 hover:bg-master-tint/40 hover:text-master",
  },
  neutral: {
    active: "border-border-strong bg-surface-sunken text-text",
    inactive: "border-border text-text-2 hover:border-border-strong hover:bg-surface-sunken",
  },
  info: {
    active: "border-info bg-info-tint text-info",
    inactive:
      "border-border text-text-2 hover:border-info/40 hover:bg-info-tint/40 hover:text-info",
  },
}

function VerdictButton({
  active,
  tone,
  icon,
  label,
  onClick,
}: {
  active: boolean
  tone: "master" | "neutral" | "info"
  icon: React.ReactNode
  label: string
  onClick: () => void
}): JSX.Element {
  const styles = VERDICT_TONE[tone]
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-7.5 items-center gap-1.5 rounded-md border px-2.5 text-body-sm font-medium transition-colors duration-120 ease-out",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--brand)]",
        active ? styles.active : styles.inactive,
      )}
    >
      <span className="[&_svg]:size-3.5" aria-hidden="true">
        {icon}
      </span>
      {label}
    </button>
  )
}

/**
 * Reword a finding's claim.
 *
 * Claim only: `PATCH .../summary` is the one field the API overlays, so
 * offering reasoning and remediation here would collect edits and discard
 * them. Both stay visible in the read view, where they are the pipeline's own
 * account of the finding.
 */
function EditForm({
  finding,
  onCancel,
  onSave,
}: {
  finding: Finding
  onCancel: () => void
  onSave: (patch: { claim: string }) => void
}): JSX.Element {
  const [claim, setClaim] = React.useState(finding.claim)

  const canSave = claim.trim().length > 0

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (!canSave) return
        onSave({ claim: claim.trim() })
      }}
      className="space-y-2.5"
    >
      <FormField
        label="Claim"
        required
        hint="What the client report prints. The reasoning and remediation below are the pipeline's and are not edited here."
      >
        <Textarea
          minRows={2}
          value={claim}
          onChange={(e) => setClaim(e.currentTarget.value)}
          autoFocus
        />
      </FormField>
      <div className="flex items-center gap-1.5 border-t border-border-soft pt-3">
        <Button type="submit" size="sm" variant="primary" disabled={!canSave}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

/**
 * Two-row evidence: master on top with a subtle green tint, printed below
 * with an amber tint. The eyebrow column sits at a fixed 78px, so quoted
 * text on the right aligns cleanly across findings.
 */
function EvidencePair({
  evidence,
}: {
  evidence: { master: string | null; printed: string | null }
}): JSX.Element {
  // `null` when a side is absent or the pair is too large to diff cheaply;
  // the rows then show the quotes plainly.
  const parts = React.useMemo(
    () =>
      evidence.master !== null && evidence.printed !== null
        ? diffWords(evidence.master, evidence.printed)
        : null,
    [evidence.master, evidence.printed],
  )
  return (
    <div className="overflow-hidden rounded-lg border border-border">
      <EvidenceRow
        label="Master"
        tone="master"
        text={evidence.master}
        parts={parts === null ? null : sideParts(parts, "master")}
      />
      <EvidenceRow
        label="Printed"
        tone="print"
        text={evidence.printed}
        parts={parts === null ? null : sideParts(parts, "printed")}
      />
    </div>
  )
}

function EvidenceRow({
  label,
  tone,
  text,
  parts,
}: {
  label: string
  tone: "master" | "print"
  text: string | null
  /** The word-level marks for this side, or null to show the quote plainly. */
  parts: DiffPart[] | null
}): JSX.Element {
  const [labelBg, labelFg] =
    tone === "master" ? ["bg-master-tint", "text-master"] : ["bg-print-tint", "text-print"]
  // Only the label carries the tint. The quoted text is what the reviewer is
  // comparing word by word, and text on a wash is harder to read than text on
  // the surface — the colour's job is to say which document this came from.
  const contentBg = "bg-surface"
  return (
    <div className="grid grid-cols-[78px_minmax(0,1fr)] border-b border-border-soft last:border-b-0">
      <span
        className={cn(
          "px-2.5 py-2 font-id text-[10.5px] font-medium uppercase tracking-[0.08em]",
          labelBg,
          labelFg,
        )}
      >
        {label}
      </span>
      <span
        className={cn(
          "min-w-0 whitespace-pre-wrap break-words px-3 py-2 font-doc text-[15px] leading-[1.4] text-text",
          contentBg,
        )}
      >
        {text === null ? (
          <span className="text-body-sm italic text-text-3">(absent)</span>
        ) : parts === null ? (
          text
        ) : (
          parts.map((part, i) =>
            part.op === "same" ? (
              <React.Fragment key={i}>{part.text}</React.Fragment>
            ) : (
              <mark
                key={i}
                className={cn(
                  "rounded-[2px] px-0.5",
                  part.op === "removed" ? "bg-master-tint text-master" : "bg-print-tint text-print",
                )}
              >
                {part.text}
              </mark>
            ),
          )
        )}
      </span>
    </div>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="grid gap-1">
      <span className="font-id text-[10.5px] font-medium uppercase tracking-[0.1em] text-text-3">
        {label}
      </span>
      <div>{children}</div>
    </div>
  )
}
